import { firstValueFrom } from "rxjs";
import { HttpParams } from "@angular/common/http";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { modalXlOptions } from "app/core/constants";
import { CustomStore } from "devextreme/common/data";
import { Component, Inject, OnInit } from "@angular/core";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { USER_AUTHENTICATION_SERVICE, UserAuthInterface } from "app/core/services";
import { ProductDetailFilterModalComponent } from "../product-detail-filter-modal/product-detail-filter-modal.component";
import { InvoiceDetailsModalComponent } from "../invoice-details-component/invoice-details-modal.component";
import { CustomerModel, InvoiceModel, ProductHeaderModel } from "app/core/models";
import { Router } from "@angular/router";

@Component({
    selector: 'app-product-filter-list-modal',
    templateUrl: './product-filter-list-modal.component.html',
    standalone: false
})
export class ProductListModalComponent implements OnInit {
    //#region Feilds
    searchText: string;
    dataSource: CustomStore;
    productDetails: ProductHeaderModel[] = [];
    invoiceDetails: InvoiceModel[] = [];
    customerDetails: CustomerModel[] = [];
    itemsPerPage: number = 5;

    productTotalPages: number = 0;
    productCurrentPage: number = 1;
    paginatedProductData: ProductHeaderModel[] = [];

    invoiceTotalPages: number = 0;
    invoiceCurrentPage: number = 1;
    paginatedInvoiceData: InvoiceModel[] = [];

    customerTotalPages: number = 0;
    customerCurrentPage: number = 1;
    paginatedCustomerData: CustomerModel[] = [];

    selectedInvoiceNo: string = '';

    //#endregion

    //#region Constructor
    constructor(
        private _router: Router,
        public activeModal: NgbActiveModal,
        private _loaderService: NgxUiLoaderService,
        @Inject(NgbModal) private _ngbModalService: NgbModal,
        @Inject(USER_AUTHENTICATION_SERVICE) private _userAuthInterface: UserAuthInterface,

    ) { }
    //#endregion

    //#region Utilities


    getProductList() {
        this._loaderService.start();
        this._userAuthInterface.getAllProductInventory(this.searchText).subscribe({
            next: (res) => {
                this.productDetails = res?.data?.productHeaders ?? [];
                this.invoiceDetails = res?.data?.invoiceDetails ?? [];
                this.customerDetails = res?.data?.customerDetails ?? [];
                this.productCurrentPage = 1;
                this.invoiceCurrentPage = 1;
                this.customerCurrentPage = 1;
                this.updateProductPaginatedData();
                this.updateInvoicePaginatedData();
                this.updateCustomerPaginatedData();
                this._loaderService.stop();
            },
            error: (error) => {
                this.productDetails = [];
                this.invoiceDetails = [];
                this.customerDetails = [];
                this.updateProductPaginatedData();
                this.updateInvoicePaginatedData();
                this.updateCustomerPaginatedData();
                this._loaderService.stop();
                console.error('Error fetching product inventory:', error);
            }
        });
    }
    //#endregion

    //#region Methods
    ngOnInit(): void {
        this.getProductList();
    }

    cancelPopup() {
        this.activeModal.close();
    }

    updateProductPaginatedData() {
        this.productTotalPages = Math.ceil(this.productDetails.length / this.itemsPerPage);
        const startIndex = (this.productCurrentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        this.paginatedProductData = this.productDetails.slice(startIndex, endIndex);
    }

    updateInvoicePaginatedData() {
        this.invoiceTotalPages = Math.ceil(this.invoiceDetails.length / this.itemsPerPage);
        const startIndex = (this.invoiceCurrentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        this.paginatedInvoiceData = this.invoiceDetails.slice(startIndex, endIndex);
    }

    updateCustomerPaginatedData() {
        this.customerTotalPages = Math.ceil(this.customerDetails.length / this.itemsPerPage);
        const startIndex = (this.customerCurrentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        this.paginatedCustomerData = this.customerDetails.slice(startIndex, endIndex);

    }

    get productStartIndex(): number {
        if (!this.productDetails.length) {
            return 0;
        }
        return (this.productCurrentPage - 1) * this.itemsPerPage + 1;
    }

    get productEndIndex(): number {
        if (!this.productDetails.length) {
            return 0;
        }
        return Math.min(this.productCurrentPage * this.itemsPerPage, this.productDetails.length);
    }

    get invoiceStartIndex(): number {
        if (!this.invoiceDetails.length) {
            return 0;
        }
        return (this.invoiceCurrentPage - 1) * this.itemsPerPage + 1;
    }

    get invoiceEndIndex(): number {
        if (!this.invoiceDetails.length) {
            return 0;
        }
        return Math.min(this.invoiceCurrentPage * this.itemsPerPage, this.invoiceDetails.length);
    }

    get customerStartIndex(): number {
        if (!this.customerDetails.length) {
            return 0;
        }
        return (this.customerCurrentPage - 1) * this.itemsPerPage + 1;
    }

    get customerEndIndex(): number {
        if (!this.customerDetails.length) {
            return 0;
        }
        return Math.min(this.customerCurrentPage * this.itemsPerPage, this.customerDetails.length);
    }

    onProductPageChanged(event: any) {
        this.productCurrentPage = event.page;
        this.updateProductPaginatedData();
    }

    onInvoicePageChanged(event: any) {
        this.invoiceCurrentPage = event.page;
        this.updateInvoicePaginatedData();
    }

    onCustomerPageChanged(event: any) {
        this.customerCurrentPage = event.page;
        this.updateCustomerPaginatedData();
    }

    onItemsPerPageChange(event: any) {
        this.itemsPerPage = Number(event.target.value);
        this.productCurrentPage = 1;
        this.invoiceCurrentPage = 1;
        this.customerCurrentPage = 1;
        this.updateProductPaginatedData();
        this.updateInvoicePaginatedData();
        this.updateCustomerPaginatedData();
    }

    getProductDetails(data: any) {
        const modalRef = this._ngbModalService.open(ProductDetailFilterModalComponent, modalXlOptions);
        modalRef.componentInstance.productDetails = data;
        modalRef.result.then(
            (result) => {
                if (result == 'navigate') {
                    this.cancelPopup();
                }
            }
        ).catch((error) => {
            console.error('Error opening modal:', error);
        });
    }

    viewInvoiceDetails(data: any) {
        const modalRef = this._ngbModalService.open(InvoiceDetailsModalComponent, modalXlOptions);
        modalRef.componentInstance.invoiceDetails = data?.invoiceItemDetails ?? [];
    }

    isInvoiceSelected(data: InvoiceModel): boolean {
        return this.selectedInvoiceNo === data.invoiceNo;
    }

    onInvoiceCheckChange(event: any, data: InvoiceModel) {
        this.selectedInvoiceNo = event.target.checked ? data.invoiceNo : '';
    }

    onReturnAction(type: 'RETURN' | 'BUYBACK' | 'EXCHANGE') {
        if (!this.selectedInvoiceNo) return;
        const invoiceNo = this.selectedInvoiceNo;
        const originURL = this._router.url;
        const target = '/return-order/return-to-franchise';
        this.activeModal.close();

        const go = () => this._router.navigate([target], {
            state: { invoiceNo, returnType: type, originURL }
        });

        if (this._router.url.split('?')[0] === target) {
            this._router.navigateByUrl('/', { skipLocationChange: true }).then(go);
        } else {
            go();
        }
    }
    //#endregion
}